import { memo, useMemo, useRef, useContext, useEffect } from 'react';

import { Icon, SelectWrapper } from '@Sage/hoi-poi-ui';
import { generateUUID } from '@Sage/ssm-utils';

import { getNextBoxCount } from 'containers/settings/AutomationSettings/utils';
import { getLiteral } from 'utils/getLiteral';

import { AutomationBuilderCanvasContext } from '../../AutomationBuilderCanvasContext';
import './styles.scss';

const AutomationBuilderAddNodesButton = memo(({ parentId }) => {
    const { setNodes, setEdges, data, onChange } = useContext(AutomationBuilderCanvasContext);
    const dataRef = useRef(data);
    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const addNodesButtonRef = useRef(null);
    const selectWrapperProps = useMemo(
        () => ({
            children: <Icon name="addPlus" />,
            onChange: (value) => {
                const newNodeId = generateUUID();

                // Find the parent node in the data structure
                const findParentPath = (obj, targetId, currentPath = '') => {
                    if (obj.id === targetId) {
                        return currentPath;
                    }

                    if (Array.isArray(obj.next)) {
                        for (let i = 0; i < obj.next.length; i++) {
                            const childPath = currentPath
                                ? `${currentPath}.next.${i}`
                                : `next.${i}`;
                            const result = findParentPath(obj.next[i], targetId, childPath);
                            if (result) return result;
                        }
                    }

                    return null;
                };

                // Create the new node data structure
                const parentPath = findParentPath(dataRef.current?.scene, parentId);
                const newNodeData = {
                    id: newNodeId,
                    type: value?.value,
                    name:
                        value?.value === 'conditions'
                            ? 'conditions_logic'
                            : 'update_entity_related',
                    parameters: {},
                    next: [],
                };

                // Add the new node to the parent's next array
                const fullParentPath = parentPath ? `scene.${parentPath}.next` : 'scene.next';
                onChange(fullParentPath, (currentNext) => {
                    const position = getNextBoxCount(value?.value);
                    return Array.isArray(currentNext)
                        ? [...currentNext, { ...newNodeData, position }]
                        : [{ ...newNodeData, position }];
                });

                // Create new node based on selected value
                setTimeout(() => {
                    const newNode = {
                        id: newNodeId,
                        position: { x: 0, y: 0 },
                        style: { opacity: 0 },
                        type: value?.value,
                        name:
                            value?.value === 'conditions'
                                ? 'conditions_logic'
                                : 'update_entity_related',
                        data: {},
                    };

                    // Add the new node to the canvas
                    setNodes((prevNodes) => [...prevNodes, newNode]);

                    setEdges((prevEdges) => [
                        ...prevEdges,
                        {
                            id: `edge-${parentId}-${newNodeId}`,
                            source: parentId,
                            target: newNodeId,
                            hidden: true,
                            type: 'buttonedge',
                        },
                    ]);
                });

                // dismiss popover
                addNodesButtonRef.current?.querySelector('span')?.click();
            },
            options: [
                {
                    label: getLiteral('label_condition'),
                    iconType: 'deviceHub',
                    value: 'conditions',
                },
                {
                    label: getLiteral('label_action'),
                    iconType: 'automations',
                    value: 'action',
                },
            ],
        }),
        [onChange, parentId, setEdges, setNodes],
    );

    return (
        <div className="fm-automation-builder__add-nodes-button" ref={addNodesButtonRef}>
            <SelectWrapper {...selectWrapperProps} />
        </div>
    );
});

export default AutomationBuilderAddNodesButton;
